/**
 * Allys Services Section
 * 
 * Modern "What We Offer?" accordion layout
 * Clean, minimalist design with light gray inactive items
 */

'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

export interface Service {
  id: string;
  number?: string;
  title: string;
  description: string;
  expandedContent?: string;
  link?: string;
  linkText?: string;
  icon?: string;
  image?: string;
}

export interface AllysServicesSectionProps {
  /**
   * Section title
   * @default 'What We Offer?'
   */
  title?: string;
  
  /**
   * Section subtitle
   */
  subtitle?: string;
  
  /**
   * Array of services to display
   */
  services?: Service[];
  
  /**
   * Featured image URL to display in center
   */
  featuredImage?: string;
  
  /**
   * Allow multiple services to be expanded at once
   * @default false
   */
  allowMultiple?: boolean;
  
  /**
   * Default expanded service ID
   */
  defaultExpanded?: string;
  
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys Services Section Component
 * 
 * Clean accordion layout matching the "What We Offer?" design
 */
export function AllysServicesSection({
  title,
  subtitle,
  services,
  featuredImage,
  allowMultiple = false,
  defaultExpanded = '2',
  messages,
  locale: _locale
}: AllysServicesSectionProps) {
  // Get translations from messages
  const servicesMsg = messages?.services as Record<string, unknown> | undefined;
  const defaultServicesMsg = servicesMsg?.defaultServices as Record<string, Record<string, unknown>> | undefined;
  
  // Build translated title and subtitle
  const displayTitle = title || safeGetString(servicesMsg?.whatWeOffer, 'What We Offer?');
  const displaySubtitle = subtitle || safeGetString(servicesMsg?.whatWeOfferSubtitle, 'Buying or selling a property can be overwhelming — we make it effortless.');
  const servicesLabel = safeGetString(servicesMsg?.title, 'Services');
  
  // Build default services from messages if no services prop provided
  const defaultServices: Service[] = services || [
    {
      id: '1',
      number: '01',
      title: safeGetString(defaultServicesMsg?.homeBuying?.title, 'Home Buying Guidance'),
      description: safeGetString(defaultServicesMsg?.homeBuying?.description, 'Navigate your purchase with confidence — from first viewing to final walkthrough, we handle the complexity so you can focus on finding your perfect home.'),
    },
    {
      id: '2',
      number: '02',
      title: safeGetString(defaultServicesMsg?.valuation?.title, 'Property Valuation'),
      description: safeGetString(defaultServicesMsg?.valuation?.description, 'Get an accurate, data-driven assessment of your property\'s market value backed by comprehensive analysis of recent sales, local trends, and unique property characteristics.'),
    },
    {
      id: '3',
      number: '03',
      title: safeGetString(defaultServicesMsg?.investment?.title, 'Investment Advisory'),
      description: safeGetString(defaultServicesMsg?.investment?.description, 'Unlock the potential of real estate investment with expert analysis on rental yields, capital appreciation, and emerging market opportunities tailored to your financial goals.'),
    },
    {
      id: '4',
      number: '04',
      title: safeGetString(defaultServicesMsg?.closings?.title, 'Transaction Management'),
      description: safeGetString(defaultServicesMsg?.closings?.description, 'Experience stress-free closings with our end-to-end transaction coordination — we manage timelines, documentation, and communication with all parties to ensure a smooth handover.'),
    },
  ];
  const [expandedIds, setExpandedIds] = useState<string[]>(
    defaultExpanded ? [defaultExpanded] : []
  );

  const toggleService = (serviceId: string) => {
    setExpandedIds((prev) => {
      if (prev.includes(serviceId)) {
        return prev.filter((id) => id !== serviceId);
      } else {
        if (allowMultiple) {
          return [...prev, serviceId];
        } else {
          return [serviceId];
        }
      }
    });
  };

  const isExpanded = (serviceId: string) => expandedIds.includes(serviceId);

  return (
    <section className="relative py-20 lg:py-32 bg-white overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 lg:mb-20">
          {/* Services Label */}
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-gray-900" />
            <span 
              className="text-xs font-normal tracking-wider text-gray-900 font-inter"
            >
              {servicesLabel}
            </span>
          </div>

          <h2 
            className="text-5xl sm:text-6xl lg:text-7xl font-normal text-gray-900 mb-6 tracking-tight font-inter"
          >
            {displayTitle}
          </h2>
          
          {displaySubtitle && (
            <p 
              className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed font-light font-inter"
            >
              {displaySubtitle}
            </p>
          )}
        </div>

        {/* Accordion Container with Featured Image */}
        <div className="relative max-w-5xl mx-auto">
          {/* Featured Image - Center Overlay (Desktop Only) */}
          {featuredImage && (
            <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 xl:w-96 h-80 xl:h-96 z-10 pointer-events-none">
              <div className="relative w-full h-full">
                {/* Image container with subtle shadow */}
                <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl">
                  <Image
                    src={featuredImage}
                    alt="Service illustration"
                    fill
                    className="object-cover"
                    sizes="(max-width: 1280px) 320px, 384px"
                    priority
                  />
                </div>
              </div>
            </div>
          )}

          {/* Services Accordion - Clean List */}
          <div className="relative">
            {defaultServices.map((service, index) => {
              const expanded = isExpanded(service.id);
              
              return (
                <div 
                  key={service.id} 
                  className={`border-b border-gray-200 last:border-0 transition-colors duration-200 ${
                    index === 0 ? 'border-t' : ''
                  }`}
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => toggleService(service.id)}
                    className="w-full text-left py-8 lg:py-10 flex items-center justify-between gap-8 transition-all duration-200 group"
                  >
                    {/* Title */}
                    <div className="flex-1 min-w-0">
                      <h3 
                        className={`text-2xl lg:text-4xl font-light tracking-tight transition-all duration-200 font-inter ${
                          expanded 
                            ? 'text-gray-900 font-normal' 
                            : 'text-gray-500 group-hover:text-gray-600'
                        }`}
                      >
                        {service.title}
                      </h3>
                    </div>

                    {/* Number - Right Side */}
                    {service.number && (
                      <div 
                        className={`text-2xl lg:text-4xl font-light tracking-tight transition-colors duration-200 font-inter ${
                          expanded ? 'text-gray-900' : 'text-gray-500 group-hover:text-gray-600'
                        }`}
                      >
                        {service.number}
                      </div>
                    )}
                  </button>
                  
                  {/* Expanded Content */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="pb-8 lg:pb-10 pr-4 md:pr-20">
                      <p 
                        className="text-base lg:text-lg text-gray-600 leading-relaxed mb-6 font-light font-inter"
                      >
                        {service.description}
                      </p>
                      {service.link && (
                        <Link
                          href={service.link}
                          className="inline-flex items-center gap-2 text-base font-normal text-gray-900 hover:gap-3 transition-all duration-200 group/link underline decoration-1 underline-offset-4 font-inter"
                        >
                          <span>{service.linkText || 'More info'}</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Featured Image */}
          {featuredImage && (
            <div className="lg:hidden mt-12 relative w-full max-w-md mx-auto aspect-square">
              <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-xl">
                <Image
                  src={featuredImage}
                  alt="Service illustration"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 448px"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
